import React, { useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { MoonLoader } from "react-spinners";
import "../../assets/css/aunjai-otp.css";
import "../../assets/css/Footer.css";
import "../../assets/css/registerNation.css";
import TextInput from "../../components/form/TextInput";
import DatePicker from "../../components/ui/DatePicker/DatePicker";
import LanguageSwitcher from "../../components/ui/LanguageSwitcher/LanguageSwitcher";
import OtpInput from "../LoanProcess/Otp";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { useUser } from "../../hooks/useUser";
import { useAccountRegisterStore } from "../../stores/useAccountRegisterStore";
import {
  refreshToken,
  signIn,
} from "../../features/auth/utils/auth.util";
import { post } from "../../services/config";

export const NATIONALITIES = {
  THAI: "thai",
  NON_THAI: "non-thai",
};

const PHASES = {
  PHONE_INPUT: "PHONE_INPUT",
  OTP_VERIFY: "OTP_VERIFY",
  ALREADY_REGISTERED: "ALREADY_REGISTERED",
};

const MAX_PHONE_NUMBER_LENGTH = 10;

const phoneValidationSchema = Yup.object({
  phoneNumber: Yup.string()
    .required("กรุณากรอกเบอร์โทรศัพท์")
    .matches(/^0\d{9}$/, "กรุณากรอกเบอร์โทรศัพท์ 10 หลักที่ขึ้นต้นด้วย 0"),
});

const loginValidationSchema = Yup.object({
  idCard: Yup.string()
    .required("กรุณากรอกเลขบัตรประชาชน")
    .test("13-digit", "กรุณากรอกเลขบัตรประชาชน 13 หลัก", (value) =>
      value ? value.length === 13 : false
    ),
  birthDate: Yup.date()
    .required("กรุณาเลือกวัน เดือน ปีเกิด")
    .max(
      dayjs().subtract(20, "year").toDate(),
      "อายุต้องมากกว่า 20 ปี"
    ),
});

const RegisterNation = () => {
  const { t } = useTranslation("register");
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { isLoggedIn, refetchUser } = useUser();

  const [currentPhase, setCurrentPhase] = useState(PHASES.PHONE_INPUT);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState(null);

  const setIdCard = useAccountRegisterStore((state) => state.setIdCard);
  const setDob = useAccountRegisterStore((state) => state.setDob);
  const setIsForeigner = useAccountRegisterStore(
    (state) => state.setIsForeigner
  );
  const setStorePhoneNumber = useAccountRegisterStore((state) => state.setPhoneNumber);

  if (isLoggedIn) {
    navigate("/profile");
  }

  const handlePhoneSubmit = (values) => {
    setPhoneNumber(values.phoneNumber);
    setCurrentPhase(PHASES.OTP_VERIFY);
  };

  const handleOtpSuccess = async () => {
    setLoading(true);

    try {
      const response = await post({
        url: "/api/aunjai/checkCustomerByPhone",
        body: { phone: phoneNumber },
      });

      const resultData = Array.isArray(response.resultData)
        ? response.resultData[0]
        : response.resultData;
      const customerFound =
        response.resultCode === "20000" && resultData?.found === true;

      if (customerFound) {
        if (resultData.id_card && resultData.date_of_birth) {
          // Auto-login bypass — skip ID card + DOB form
          await handleLoginSubmit({
            idCard: resultData.id_card,
            birthDate: resultData.date_of_birth,
          });
        } else {
          // Fallback — show manual login form
          setCustomerData(resultData);
          setCurrentPhase(PHASES.ALREADY_REGISTERED);
        }
      } else {
        // New customer — store phone and navigate to registration form
        if (setStorePhoneNumber) {
          setStorePhoneNumber(phoneNumber);
        }
        navigate("/register/form");
      }
    } catch (error) {
      alert(t("alert2"));
      setCurrentPhase(PHASES.PHONE_INPUT);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSubmit = async (values) => {
    setLoading(true);

    try {
      await refreshToken({
        idCard: values.idCard,
        isForeigner: false,
      });

      const result = await signIn({
        idCard: values.idCard,
        dateOfBirth: values.birthDate,
        isForeigner: false,
      });

      const wasSignInSuccess = result?.response?.resultCode === "20000";

      if (wasSignInSuccess) {
        const urlParams = new URLSearchParams(window.location.search);
        const redirectTokenUrl = urlParams.get("token");

        if (redirectTokenUrl) {
          window.location.href = redirectTokenUrl;
        } else {
          refetchUser();
        }
      } else {
        throw result?.response;
      }
    } catch (error) {
      if (error?.resultCode === "40401") {
        alert(t("alert"));
      } else {
        alert(error?.errors?.error || error?.message || t("alert2"));
      }
    } finally {
      setLoading(false);
    }
  };

  const today = dayjs();
  const minDate = today.subtract(20, "year").format("YYYY-MM-DD");

  // Phase: OTP Verification
  if (currentPhase === PHASES.OTP_VERIFY) {
    if (loading) {
      return (
        <div
          id="PageRegisterNation"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <MoonLoader size={40} color="#065f4f" loading={loading} />
        </div>
      );
    }

    return (
      <OtpInput
        phoneNumber={phoneNumber}
        onOtpSuccess={handleOtpSuccess}
        onBack={() => setCurrentPhase(PHASES.PHONE_INPUT)}
        allowOtpByPass
      />
    );
  }

  return (
    <div id="PageRegisterNation">
      <div className="wrapContent">
        <div className="bLanguage">
          <LanguageSwitcher />
        </div>
        <div id="pRegisterNation">
          <div className="bRegisterNation">
            <div className="bContent">
              <div className="bBanner">
                <img src="/assets/img/ci/icon/icon-aunjai.svg" alt="CTS" />
                <h3>{t("aunjaiWelcome")}</h3>
              </div>
              <div className="bAction">
                {currentPhase === PHASES.PHONE_INPUT && (
                  <PhoneInputPhase
                    t={t}
                    isDesktop={isDesktop}
                    onSubmit={handlePhoneSubmit}
                  />
                )}

                {currentPhase === PHASES.ALREADY_REGISTERED && (
                  <AlreadyRegisteredPhase
                    t={t}
                    isDesktop={isDesktop}
                    loading={loading}
                    minDate={minDate}
                    onSubmit={handleLoginSubmit}
                    onBack={() => setCurrentPhase(PHASES.PHONE_INPUT)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Phase 1: Phone number input
 */
const PhoneInputPhase = ({ t, isDesktop, onSubmit }) => {
  return (
    <Formik
      initialValues={{ phoneNumber: "" }}
      validationSchema={phoneValidationSchema}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, errors }) => (
        <>
          <Form className="bInput">
            <div className="bText">
              <p>{t("enterPhone")}</p>
            </div>
            <TextInput
              className="bId"
              maxLength={MAX_PHONE_NUMBER_LENGTH}
              label={t("tel")}
              name="phoneNumber"
              placeholder={t("telNum")}
              errorMessage={errors.phoneNumber}
              type="tel"
              required
            />
          </Form>

          <footer
            className="footer"
            style={
              isDesktop
                ? {
                    position: "relative",
                    borderRadius: 0,
                    boxShadow: "none",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "transparent",
                    display: "flex",
                  }
                : undefined
            }
          >
            <div
              className="bBtn"
              style={
                isDesktop
                  ? {
                      display: "flex",
                      justifyContent: "center",
                      maxWidth: "300px",
                      width: "100%",
                    }
                  : undefined
              }
            >
              <button type="button" onClick={handleSubmit}>
                {t("getOtp")}
              </button>
            </div>
          </footer>
        </>
      )}
    </Formik>
  );
};

/**
 * Phase 3a: Customer already registered — show login form (ID card + DOB)
 */
const AlreadyRegisteredPhase = ({
  t,
  isDesktop,
  loading,
  minDate,
  onSubmit,
  onBack,
}) => {
  return (
    <>
      <div className="bText" style={{ textAlign: "center", marginBottom: 16 }}>
        <p style={{ fontWeight: "bold", fontSize: "1.1em", color: "#065f4f" }}>
          {t("alreadyRegistered") || "คุณเคยลงทะเบียนแล้ว"}
        </p>
        <p style={{ marginTop: 8 }}>
          {t("loginToAccess") || "กรุณากรอกข้อมูลเพื่อเข้าสู่ระบบ"}
        </p>
      </div>

      <Formik
        initialValues={{ idCard: "", birthDate: "" }}
        validationSchema={loginValidationSchema}
        onSubmit={onSubmit}
      >
        {({ handleSubmit, errors }) => (
          <>
            <Form className="bInput">
              <TextInput
                className="bId"
                maxLength={13}
                label={t("id")}
                name="idCard"
                errorMessage={errors.idCard}
                type="text"
                required
              />

              <DatePicker
                label={t("bDate")}
                name="birthDate"
                defaultValue={minDate}
                max={minDate}
                format="dd/mm/yyyy"
                errorMessage={errors.birthDate}
                required
              />
            </Form>

            <footer
              className="footer"
              style={
                isDesktop
                  ? {
                      position: "relative",
                      borderRadius: 0,
                      boxShadow: "none",
                      justifyContent: "center",
                      alignItems: "center",
                      backgroundColor: "transparent",
                      display: "flex",
                    }
                  : undefined
              }
            >
              <div
                className="bBtn bActionButton"
                style={
                  isDesktop
                    ? {
                        display: "flex",
                        justifyContent: "center",
                        maxWidth: "300px",
                        width: "100%",
                      }
                    : undefined
                }
              >
                <button type="button" className="outline" onClick={onBack}>
                  {t("back")}
                </button>
                <button type="button" onClick={handleSubmit}>
                  {!loading ? (
                    <>{t("confirm")}</>
                  ) : (
                    <MoonLoader size={20} color="#065f4f" loading={loading} />
                  )}
                </button>
              </div>
            </footer>
          </>
        )}
      </Formik>
    </>
  );
};

export default RegisterNation;
